import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { media, zLayers } from '../../styles/theme';

/**
 * 可搜索的单选下拉框，用来替换原生 <select>。
 *
 * 输入文字过滤选项 → 点选 → 可以清除，或者直接继续输入搜别的选项来替换。
 *
 * options: [{ value, label, description?, group?, disabled? }]
 *   - group：连续相同 group 的选项上方会显示一个小标题，调用方负责把同组的排在一起
 *   - description：选项下方的灰色说明，也参与搜索
 * onChange(value, option)：清除时 value 为 ''、option 为 null；自定义值时 option 为 null
 *
 * 菜单渲染到 body 上（position: fixed），不会被外层的 overflow 裁掉，在弹窗里也能用。
 * 注意 React 的合成事件仍沿组件树冒泡：放在可点击的容器里时，由调用方在外层 stopPropagation。
 */

const ACCENT = '#F1AF59';
const MENU_MAX_HEIGHT = 320;
const MENU_MIN_HEIGHT = 120;
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;
// 选项不多于这个数时一眼就能看完，默认不开搜索 —— iPad 上一点就弹出键盘反而碍事
const AUTO_SEARCH_THRESHOLD = 6;

const SIZES = {
  md: { height: 40, fontSize: 14, padX: 12, optionPadY: 9 },
  sm: { height: 30, fontSize: 12, padX: 8, optionPadY: 6 },
};

const Control = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${p => SIZES[p.$size].height}px;
  background: ${p => (p.$disabled ? '#f3f4f6' : '#fff')};
  border: 1px solid ${p => (p.$open ? ACCENT : '#d1d5db')};
  border-radius: 6px;
  box-shadow: ${p => (p.$open ? '0 0 0 3px rgba(241, 175, 89, 0.18)' : 'none')};
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${p => (p.$disabled ? '#d1d5db' : ACCENT)};
  }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  height: ${p => SIZES[p.$size].height - 2}px;
  margin: 0;
  padding: 0 4px 0 ${p => SIZES[p.$size].padX}px;
  border: none;
  background: transparent;
  font-size: ${p => SIZES[p.$size].fontSize}px;
  font-family: inherit;
  color: #1f2937;
  cursor: inherit;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${p => (p.$placeholderIsValue ? '#6b7280' : '#9ca3af')};
  }

  &:disabled {
    color: #6b7280;
    -webkit-text-fill-color: #6b7280;
  }

  ${media.phone} {
    font-size: 16px;
  }
`;

const IconButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => SIZES[p.$size].height - 10}px;
  height: ${p => SIZES[p.$size].height - 10}px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  font-size: ${p => SIZES[p.$size].fontSize + 4}px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: #374151;
    background: #f3f4f6;
  }
`;

const Chevron = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => SIZES[p.$size].height - 12}px;
  margin-right: 4px;
  color: #9ca3af;
  font-size: ${p => SIZES[p.$size].fontSize - 2}px;
  pointer-events: none;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  transition: transform 0.15s;
`;

const Menu = styled.div`
  position: fixed;
  z-index: ${zLayers.popover};
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.16);
  text-align: left;
`;

const GroupHeader = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af;

  &:not(:first-child) {
    margin-top: 4px;
    border-top: 1px solid #f3f4f6;
  }
`;

const optionBackground = ({ $active, $selected }) => {
  if ($active && $selected) return '#fde7c2';
  if ($selected) return '#fef3e2';
  if ($active) return '#f3f4f6';
  return 'transparent';
};

const Option = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: ${p => SIZES[p.$size].optionPadY}px ${p => SIZES[p.$size].padX + 2}px;
  font-size: ${p => SIZES[p.$size].fontSize}px;
  line-height: 1.35;
  color: ${p => (p.$disabled ? '#9ca3af' : '#1f2937')};
  background: ${optionBackground};
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  -webkit-tap-highlight-color: transparent;
`;

const OptionText = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;

  .label {
    font-weight: ${p => (p.$selected ? 600 : 400)};
  }

  .description {
    display: block;
    margin-top: 2px;
    font-size: 0.9em;
    color: #6b7280;
  }
`;

const Check = styled.span`
  flex-shrink: 0;
  color: #b7791f;
  font-weight: 700;
`;

const CustomOption = styled(Option)`
  color: #92400e;

  &:not(:first-child) {
    border-top: 1px solid #f3f4f6;
  }
`;

const Empty = styled.div`
  padding: 12px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
`;

const isEmptyValue = v => v === '' || v === null || v === undefined;
const sameValue = (a, b) => !isEmptyValue(a) && !isEmptyValue(b) && String(a) === String(b);

// 大小写、标点都不参与匹配：搜 "end of term" 能匹配 "End-of-Term"
const searchKey = text => String(text ?? '')
  .toLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .trim();

const isSelectable = item => item && (item.kind === 'custom' || !item.option.disabled);

const firstSelectableIndex = items => items.findIndex(isSelectable);

const stepSelectableIndex = (items, from, step) => {
  const count = items.length;
  if (!count) return -1;
  for (let k = 1; k <= count; k += 1) {
    const index = (((from + step * k) % count) + count) % count;
    if (isSelectable(items[index])) return index;
  }
  return -1;
};

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  clearable = true,
  searchable = 'auto',
  allowCustomValue = false,
  noOptionsText = 'No matches',
  size = 'md',
  menuMinWidth = 220,
  id,
  className,
  style,
  'aria-label': ariaLabel,
}) => {
  const reactId = useId();
  const inputId = id || `searchable-select-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const listboxId = `${inputId}-listbox`;
  const sizeKey = SIZES[size] ? size : 'md';

  const controlRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const frameRef = useRef(0);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState(null);

  const canSearch = searchable === 'auto'
    ? allowCustomValue || options.length > AUTO_SEARCH_THRESHOLD
    : Boolean(searchable);

  const selectedOption = useMemo(
    () => (isEmptyValue(value) ? null : options.find(o => sameValue(o.value, value)) || null),
    [options, value]
  );
  const displayLabel = selectedOption
    ? String(selectedOption.label)
    : (allowCustomValue && !isEmptyValue(value) ? String(value) : '');
  const hasValue = displayLabel !== '';

  const trimmedQuery = query.trim();

  const items = useMemo(() => {
    const tokens = searchKey(trimmedQuery).split(' ').filter(Boolean);
    const matched = tokens.length === 0
      ? options
      : options.filter(o => {
        const haystack = searchKey(`${o.label} ${o.description || ''} ${o.group || ''}`);
        return tokens.every(token => haystack.includes(token));
      });
    const list = matched.map(option => ({ kind: 'option', option }));
    const exactMatch = options.some(o => String(o.label).trim().toLowerCase() === trimmedQuery.toLowerCase());
    if (allowCustomValue && trimmedQuery && !exactMatch) {
      list.push({ kind: 'custom', value: trimmedQuery });
    }
    return list;
  }, [options, trimmedQuery, allowCustomValue]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(-1);
  }, []);

  const openMenu = () => {
    if (disabled || isOpen) return;
    setQuery('');
    const allItems = options.map(option => ({ kind: 'option', option }));
    const selectedIndex = options.findIndex(o => sameValue(o.value, value));
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstSelectableIndex(allItems));
    setIsOpen(true);
  };

  const commit = (item, { keepFocus = false } = {}) => {
    if (!isSelectable(item)) return;
    if (item.kind === 'custom') {
      onChange?.(item.value, null);
    } else {
      onChange?.(item.option.value, item.option);
    }
    closeMenu();
    // 用手点选完就收起键盘；键盘操作时保留焦点，方便继续 Tab
    if (!keepFocus) inputRef.current?.blur();
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.('', null);
    setQuery('');
    setActiveIndex(firstSelectableIndex(options.map(option => ({ kind: 'option', option }))));
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleControlMouseDown = (event) => {
    // 菜单是 portal，但 React 事件仍按组件树冒泡上来 —— 只处理真正点在控件本身上的
    if (disabled || !controlRef.current?.contains(event.target)) return;
    if (!isOpen) {
      if (event.target !== inputRef.current) {
        event.preventDefault();
        inputRef.current?.focus();
      }
      openMenu();
      return;
    }
    // 已展开时：点输入框是在放光标，保持展开；点其它位置（箭头）就收起
    if (canSearch && event.target === inputRef.current) return;
    event.preventDefault();
    closeMenu();
  };

  const handleInputChange = (event) => {
    const next = event.target.value;
    if (isOpen) {
      setQuery(next);
    } else {
      // 收起状态下输入框里显示的是已选项；接着打字时只取新打的部分
      setQuery(hasValue && next.startsWith(displayLabel) ? next.slice(displayLabel.length) : next);
      setIsOpen(true);
    }
    setActiveIndex(0);
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          setActiveIndex(i => stepSelectableIndex(items, i, event.key === 'ArrowDown' ? 1 : -1));
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (isOpen) commit(items[activeIndex], { keepFocus: true });
        else openMenu();
        break;
      case ' ':
        if (!canSearch) {
          event.preventDefault();
          if (isOpen) commit(items[activeIndex], { keepFocus: true });
          else openMenu();
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          closeMenu();
        }
        break;
      case 'Tab':
        if (isOpen) closeMenu();
        break;
      default:
        break;
    }
  };

  const handleBlur = (event) => {
    const next = event.relatedTarget;
    if (next && (controlRef.current?.contains(next) || menuRef.current?.contains(next))) return;
    closeMenu();
  };

  // 过滤后选项变少时，高亮行别越界
  useEffect(() => {
    if (!isOpen) return;
    if (activeIndex >= items.length || !isSelectable(items[activeIndex])) {
      setActiveIndex(firstSelectableIndex(items));
    }
  }, [isOpen, items, activeIndex]);

  // 点到控件和菜单以外的地方就收起（只读模式下 blur 不一定触发）
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (controlRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      closeMenu();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen, closeMenu]);

  const updatePosition = useCallback(() => {
    const control = controlRef.current;
    if (!control) return;
    const rect = control.getBoundingClientRect();
    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;
    const openUpward = spaceBelow < Math.min(MENU_MAX_HEIGHT, 200) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(MENU_MIN_HEIGHT, Math.min(MENU_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow));
    const width = Math.min(Math.max(rect.width, menuMinWidth), viewportWidth - VIEWPORT_MARGIN * 2);
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, viewportWidth - width - VIEWPORT_MARGIN));

    setMenuPosition(openUpward
      ? { left, width, maxHeight, bottom: window.innerHeight - rect.top + MENU_GAP }
      : { left, width, maxHeight, top: rect.bottom + MENU_GAP });
  }, [menuMinWidth]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    const schedule = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(updatePosition);
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    window.visualViewport?.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, [isOpen, updatePosition]);

  // 键盘上下移动或刚展开时，把高亮行滚进可视区
  useEffect(() => {
    const menu = menuRef.current;
    if (!isOpen || !menu || activeIndex < 0) return;
    const row = menu.querySelector(`[data-index="${activeIndex}"]`);
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < menu.scrollTop) {
      menu.scrollTop = Math.max(0, top - 4);
    } else if (bottom > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = bottom - menu.clientHeight + 4;
    }
  }, [activeIndex, isOpen, menuPosition]);

  const renderMenuRows = () => {
    if (items.length === 0) {
      return <Empty>{noOptionsText}</Empty>;
    }
    const rows = [];
    let currentGroup;
    items.forEach((item, index) => {
      const rowId = `${listboxId}-${index}`;
      const active = index === activeIndex;
      if (item.kind === 'custom') {
        rows.push(
          <CustomOption
            key="__custom__"
            id={rowId}
            data-index={index}
            role="option"
            aria-selected={false}
            $size={sizeKey}
            $active={active}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => commit(item)}
          >
            <OptionText>
              <span className="label">Use “{item.value}”</span>
            </OptionText>
          </CustomOption>
        );
        return;
      }
      const { option } = item;
      if (option.group && option.group !== currentGroup) {
        currentGroup = option.group;
        rows.push(<GroupHeader key={`group-${index}`}>{currentGroup}</GroupHeader>);
      }
      const selected = sameValue(option.value, value);
      rows.push(
        <Option
          key={`option-${String(option.value)}`}
          id={rowId}
          data-index={index}
          role="option"
          aria-selected={selected}
          aria-disabled={option.disabled || undefined}
          $size={sizeKey}
          $active={active}
          $selected={selected}
          $disabled={option.disabled}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => commit(item)}
        >
          <OptionText $selected={selected}>
            <span className="label">{option.label}</span>
            {option.description && <span className="description">{option.description}</span>}
          </OptionText>
          {selected && <Check aria-hidden="true">✓</Check>}
        </Option>
      );
    });
    return rows;
  };

  const inputValue = isOpen && canSearch ? query : displayLabel;
  const inputPlaceholder = isOpen && canSearch && hasValue ? displayLabel : placeholder;

  return (
    <Control
      ref={controlRef}
      className={className}
      style={style}
      $size={sizeKey}
      $open={isOpen}
      $disabled={disabled}
      onMouseDown={handleControlMouseDown}
    >
      <Input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
        autoComplete="off"
        spellCheck={false}
        $size={sizeKey}
        $placeholderIsValue={isOpen && hasValue}
        value={inputValue}
        placeholder={inputPlaceholder}
        title={!isOpen && hasValue ? displayLabel : undefined}
        readOnly={!canSearch}
        disabled={disabled}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
      {clearable && hasValue && !disabled && (
        <IconButton
          type="button"
          tabIndex={-1}
          aria-label="Clear selection"
          $size={sizeKey}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={handleClear}
        >
          ×
        </IconButton>
      )}
      <Chevron $size={sizeKey} $open={isOpen} aria-hidden="true">▼</Chevron>

      {isOpen && menuPosition && createPortal(
        <Menu
          ref={menuRef}
          id={listboxId}
          role="listbox"
          style={{
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
            top: menuPosition.top,
            bottom: menuPosition.bottom,
          }}
          onMouseDown={(event) => {
            // 保住输入框焦点；同时别让事件冒泡回 Control 把菜单关掉
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {renderMenuRows()}
        </Menu>,
        document.body
      )}
    </Control>
  );
};

export default SearchableSelect;
